import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '../Icon';
import { Logo } from '../Logo';
import { Avatar } from '../Avatar';
import { Badge, type BadgeTone } from '../Badge';
import { usePanelChrome } from './PanelChromeContext';

export type SidebarPanel = 'tenant' | 'customer' | 'admin';

export type SidebarItem =
  | { section: string; id?: never; icon?: never; label?: never }
  | {
      section?: never;
      id: string;
      icon: IconName;
      label: ReactNode;
      count?: number;
      badge?: { tone: BadgeTone; label: ReactNode };
      onClick?: () => void;
    };

export interface SidebarFooter {
  name: string;
  role: string;
  onClick?: () => void;
}

export interface SidebarProps {
  panel?: SidebarPanel;
  items: SidebarItem[];
  active?: string;
  brandLine?: ReactNode;
  footer?: SidebarFooter;
  /**
   * Optional override of the brand block. Useful for the customer-portal
   * white-label case where the tenant's brand replaces DestekGör's logo.
   */
  brand?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

/**
 * 232px-wide vertical navigation. Light theme across all three panels
 * (handoff: admin no longer dark — same surface as tenant).
 */
export function Sidebar({
  panel = 'tenant',
  items,
  active,
  brandLine,
  footer,
  brand,
  style,
  className,
}: SidebarProps) {
  // Reserved for future dark-admin variant; light by default.
  void panel;
  const { isMobile, setNavOpen } = usePanelChrome();
  const surface = 'var(--dg-surface)';
  const borderColor = 'var(--dg-border)';

  return (
    <aside
      className={className}
      style={{
        width: isMobile ? '100%' : 232,
        flexShrink: 0,
        background: surface,
        borderRight: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      <div
        style={{
          padding: '16px 16px 14px',
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        {brand ?? <Logo size={22} tone="brand" />}
        {brandLine && (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--dg-text-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {brandLine}
          </div>
        )}
      </div>

      <nav style={{ flex: 1, padding: '10px 8px', overflow: 'auto' }}>
        {items.map((it, i) => {
          if ('section' in it && it.section) {
            return (
              <div
                key={`section-${i}`}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--dg-text-4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  padding: '14px 10px 6px',
                }}
              >
                {it.section}
              </div>
            );
          }
          const item = it as Extract<SidebarItem, { id: string }>;
          const isActive = item.id === active;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                item.onClick?.();
                if (isMobile) setNavOpen(false);
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: isMobile ? '12px 10px' : '8px 10px',
                minHeight: isMobile ? 44 : undefined,
                borderRadius: 'var(--dg-r-md)',
                border: 'none',
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 500,
                fontFamily: 'inherit',
                textAlign: 'left',
                color: isActive ? 'var(--dg-brand)' : 'var(--dg-text-2)',
                background: isActive ? 'var(--dg-brand-soft)' : 'transparent',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Icon
                name={item.icon}
                size={16}
                style={{ color: isActive ? 'var(--dg-brand)' : 'var(--dg-text-3)' }}
              />
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.count != null && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: isActive ? 'white' : 'var(--dg-text-3)',
                    background: isActive ? 'var(--dg-accent-500)' : 'transparent',
                    padding: '1px 7px',
                    borderRadius: 999,
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              )}
              {item.badge && (
                <Badge size="sm" tone={item.badge.tone}>
                  {item.badge.label}
                </Badge>
              )}
            </button>
          );
        })}
      </nav>

      {footer && (
        <button
          type="button"
          onClick={footer.onClick}
          style={{
            padding: 10,
            border: 'none',
            borderTop: `1px solid ${borderColor}`,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            cursor: footer.onClick ? 'pointer' : 'default',
            fontFamily: 'inherit',
            textAlign: 'left',
          }}
        >
          <Avatar name={footer.name} size={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--dg-text-1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {footer.name}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--dg-text-3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {footer.role}
            </div>
          </div>
          <Icon name="more" size={16} style={{ color: 'var(--dg-text-3)' }} />
        </button>
      )}
    </aside>
  );
}
